import { Component, DestroyRef, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UserService } from '@core/services/user';
import {
  CreateConversationMessageRequest,
  ServiceRequestConversation,
  ServiceRequestDocument,
  ServiceRequestService,
} from '@core/services/service-request';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { validateVerticalPosition } from '@angular/cdk/overlay';

//25MB
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
//100MB
const MAX_TOTAL_SIZE_BYTES = 100 * 1024 * 1024;

//not using the file upload tool due to its size on screen for the conversation section
const ALLOWED_MIME_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg'];
const ALLOWED_EXTENSIONS = ['pdf', 'docx', 'png', 'jpg', 'jpeg'];
@Component({
  selector: 'app-conversation',
  imports: [ReactiveFormsModule
  ],
  templateUrl: './conversation.html',
  styleUrl: './conversation.scss',
})
export class Conversation {
  serviceRequestId = input.required<number>();
  serviceRequestDocuments = input<ServiceRequestDocument[]>([]);
  isLoading = input<boolean>(false);
  attachmentsUploaded = output<void>();
  //#region services 
  private serviceRequestService = inject(ServiceRequestService);
  private userService = inject(UserService);
  private destroyRef = inject(DestroyRef);
  //#endregion

  //#region form groups
  protected readonly messageForm = new FormGroup({
    message: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(5000)] })
  });
  //#endregion

  //#region signals
  conversationHistory = signal<ServiceRequestConversation[]>([]);
  isLoadingHistory = signal(false);
  selectedAttachments = signal<File[]>([]);
  attachmentError = signal<string | null>(null);
  private readonly documentsByConversationId = computed(() => {
    const documents = new Map<number, ServiceRequestDocument[]>();

    for (const document of this.serviceRequestDocuments()) {
      const conversationId = document.serviceRequestConversationId;
      if (conversationId === null) continue;

      const conversationDocuments = documents.get(conversationId) ?? [];
      conversationDocuments.push(document);
      documents.set(conversationId, conversationDocuments);
    }

    return documents;
  });
  //signal view of the message value so canSend can react to typing
  private readonly messageValue = toSignal(this.messageForm.controls.message.valueChanges, { initialValue: '' });
  //send button enable/disable
  protected readonly canSend = computed(() => {
    const messageVal = this.messageValue();
    const form = this.messageForm;
    const hasText = messageVal.trim().length > 0;
    return form.valid && hasText && !form.disabled;
  });
  //#endregion



  constructor() {
    effect(() => {
      const id = this.serviceRequestId();
      if (id) this.loadConversation(id);
    });
  }

  private loadConversation(serviceRequestId: number): void {
    this.isLoadingHistory.set(true);

    this.serviceRequestService.getServiceRequestConversation(serviceRequestId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (messages) => {
          const sorted = [...messages].sort(
            (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
          );
          this.conversationHistory.set(this.removeLegacyAttachmentSummaryMessages(sorted));
          this.isLoadingHistory.set(false);
        },
        error: () => {
          this.conversationHistory.set([]);
          this.isLoadingHistory.set(false);
        },
      });
  }

  getConversationDocuments(conversationId: number): ServiceRequestDocument[] {
    return this.documentsByConversationId().get(conversationId) ?? [];
  }

  getDocumentIcon(fileExtension: string): string {
    switch (fileExtension.toLowerCase().replace('.', '')) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'fa-file-image';
      default:
        return 'fa-file';
    }
  }

  private removeLegacyAttachmentSummaryMessages(
    messages: ServiceRequestConversation[]
  ): ServiceRequestConversation[] {
    return messages.filter((message, index) => {
      const previousMessage = messages[index - 1];
      if (
        !previousMessage ||
        message.hasAttachments ||
        !previousMessage.hasAttachments ||
        !/^Files attached:\s*/i.test(message.messageBody)
      ) {
        return true;
      }

      const isSameSender = message.senderUserId === previousMessage.senderUserId;
      const isSameMessageType = message.messageType === previousMessage.messageType;
      const timeDifference = Math.abs(
        new Date(message.createdDate).getTime() - new Date(previousMessage.createdDate).getTime()
      );

      return !(isSameSender && isSameMessageType && timeDifference <= 60_000);
    });
  }

  //#region Attachments
  openAttachmentPicker(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const newFiles = Array.from(input.files ?? []);
    if (newFiles.length === 0) return;

    const existing = this.selectedAttachments();
    const merged = [...existing];
    const errors: string[] = [];

    for (const file of newFiles) {
      const validationError = this.validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
        continue;
      }
      const isDuplicate = merged.some(f => f.name === file.name && f.size === file.size);
      if (!isDuplicate) {
        merged.push(file);
      }
    }

    const totalSize = merged.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_TOTAL_SIZE_BYTES) {
      this.attachmentError.set('Total attachment size exceeds 100 MB limit.');
      input.value = '';
      return;
    }

    this.attachmentError.set(errors.length > 0 ? errors.join(' ') : null);
    this.selectedAttachments.set(merged);
    input.value = '';
  }

  removeAttachment(file: File): void {
    this.selectedAttachments.update(files => files.filter(f => f !== file));
    this.attachmentError.set(null);
  }

  clearSelectedAttachment(input: HTMLInputElement): void {
    input.value = '';
    this.selectedAttachments.set([]);
    this.attachmentError.set(null);
  }
  //#endregion

  //#region Send Message
  sendMessage(fileInput: HTMLInputElement): void {
    if (!this.canSend()) return;

    const message = this.messageForm.controls.message.value.trim();
    const userId = this.getUserId();
    const serviceRequestId = this.serviceRequestId();
    const attachment = this.selectedAttachments();
    const hasAttachments = attachment.length > 0;

    const tempPrimary: ServiceRequestConversation = {
      serviceRequestConversationId: -1,
      serviceRequestId,
      messageType: 'Public Reply',
      messageBody: message,
      senderUserId: userId,
      isRead: false,
      readDate: null,
      readByUserId: null,
      hasAttachments,
      isNotificationSent: false,
      notificationSentDate: null,
      notificationChannel: null,
      createdDate: new Date().toISOString(),
      createdBy: userId,
      modifiedDate: null,
      modifiedBy: null,
      isSending: true,
    };

    this.conversationHistory.update((history) => [...history, tempPrimary]);
    this.messageForm.disable();

    const primaryPayload: CreateConversationMessageRequest = {
      serviceRequestId,
      messageType: 'Public Reply',
      messageBody: message,
      senderUserId: userId,
      hasAttachments,
      createdBy: userId,
      conversationDocuments: attachment.length > 0
        ? attachment.map(f => ({ file: f, description: f.name }))
        : null
    };

    this.serviceRequestService.createConversationMessage(primaryPayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedPrimaryMessage) => {
          this.conversationHistory.update((history) => [
            ...history.filter((i) => i.serviceRequestConversationId !== -1),
            savedPrimaryMessage,
          ]);
          //clear the form / allow for entry again
          this.messageForm.reset();
          this.clearSelectedAttachment(fileInput);
          this.messageForm.enable();
          if (hasAttachments) this.attachmentsUploaded.emit();
        },
        error: () => {
          this.conversationHistory.update((history) =>
            history.filter((i) => i.serviceRequestConversationId !== -1)
          );
          this.messageForm.enable();
          // TODO: show error notification to user
        },
      });
  }

  isInternalMessage(message: ServiceRequestConversation): boolean {
    return message.messageType === 'Staff Only' || message.messageType === 'Staff Reply';
  }
  //#endregion

  //#region Validation & Helpers
  private validateFile(file: File): string | null {
    if (file.size === 0) {
      return 'Selected an empty file.';
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      return 'File is too large. Maximum file size allowed: 25MB';
    }
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    const isExtensionAllowed = ALLOWED_EXTENSIONS.includes(extension);
    const isMimeAllowed = ALLOWED_MIME_TYPES.includes(file.type);
    if (!isExtensionAllowed && !isMimeAllowed) {
      return 'File type not allowed. Accepted types: PDF, JPG, PNG, DOCX.';
    }
    //validation success - no error
    return null;
  }

  getFileExtension(file: File): string {
    const ext = file.name.split('.').pop();
    return ext ? ext.toUpperCase() : '';
  }

  formatMessageDate(dateString: string): string {
    if (dateString == null) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Los_Angeles',
      hour12: true,
    });
  }

  private getUserId(): number {
    const user = this.userService.getUserInfoFromStorage();
    return user?.userId ?? 0;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }
  //#endregion
}
